// PROJECTS KANBAN — the same 13 projects, three ways of looking at them.
//
// Erez, 2026-07-25: "create three different kinds of kanban boards to show the
// same data but with different ways of seeing it." This is a CHOOSER — pick the
// lens that actually helps and we keep one.
//
// Why three LENSES rather than three skins: the projects table is already fresh
// (every row was updated within a day), and every project already has its own
// board for depth. What was missing was ALTITUDE — one line per project — and a
// way to sort by what needs Erez. So the three views differ in how they GROUP
// and how DENSE the cards are, not in colour.
//
//   Attention  — Needs you · Moving · Parked · Landing soon.  Large cards.
//   Status     — Active · Waiting · Blocked · Paused.         Compact, dense.
//   Portfolio  — Our business · Client work · Ventures · System. Medium cards.
//
// DRAG IS LOCAL FOR NOW. There is no write endpoint for projects (functions/api
// /projects only exposes GET), so a drag re-buckets in session and does not
// persist. Deliberate: adding a PATCH for two designs we will throw away is
// waste. Once Erez picks a view, one small endpoint makes that view's drag real.
//
// React Flow traps handled: `nowheel nodrag nopan` so wheel scrolls the columns
// instead of zooming the canvas and a drag does not pan the board, plus
// pointerEvents:auto because non-draggable RF nodes set pointer-events:none.
import { useMemo, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { ArrowUpRight } from "lucide-react";

const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  hair: "#E3DED4", card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
  plum: "#7A5B78", amber: "#B07A1E", red: "#C0492F", redWash: "#F7EBE8", blue: "#2F6BB0",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const EYEBROW: React.CSSProperties = {
  fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: "0.14em",
  textTransform: "uppercase", color: T.faint,
};
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export type KProject = { slug: string; name: string; status: string; owner_agent: string | null; current_focus: string | null; board_order: number | null };
export type KPlan = { agent: string; role?: string; today?: string; next?: string };
export type KDecision = { title: string; context: string; recommendation: string; owner?: string };

const BOARD_ROUTE: Record<string, string> = {
  jarvis: "/command", leo: "/marketing-board", ben: "/command", sol: "/startup-board",
  remy: "/youtube-board", emma: "/max-security-board", cleo: "/elhadegel-board",
  kai: "/elad-engineering-board", milo: "/boiling-offers-board", nova: "/flameking-manager",
  atlas: "/board", dave: "/knowledge-board", otto: "/falcon-board", hank: "/air-duct-board",
};
const STATUS_DOT: Record<string, string> = {
  active: T.green, running: T.green, waiting: T.amber,
  blocked: T.red, paused: T.faint, done: T.faint,
};

// ── the three lenses ─────────────────────────────────────────────────────────
type View = "attention" | "status" | "portfolio";
type Col = { id: string; label: string; hint: string; tone: string };

const VIEWS: Record<View, { label: string; blurb: string; cols: Col[] }> = {
  attention: {
    label: "Attention",
    blurb: "Sorted by what needs YOU. Column one is the meeting.",
    cols: [
      { id: "needs", label: "Needs you", hint: "blocked on a decision only Erez can make", tone: T.red },
      { id: "moving", label: "Moving", hint: "in flight, nothing blocking", tone: T.green },
      { id: "parked", label: "Parked", hint: "idle or awaiting direction", tone: T.faint },
      { id: "landing", label: "Landing soon", hint: "completes in the next fortnight", tone: T.plum },
    ],
  },
  status: {
    label: "Status",
    blurb: "The classic board, straight off the status field. Dense — the whole portfolio at a glance.",
    cols: [
      { id: "active", label: "Active", hint: "", tone: T.green },
      { id: "waiting", label: "Waiting", hint: "", tone: T.amber },
      { id: "blocked", label: "Blocked", hint: "", tone: T.red },
      { id: "paused", label: "Paused / done", hint: "", tone: T.faint },
    ],
  },
  portfolio: {
    label: "Portfolio",
    blurb: "Grouped the way the sidebar is — our own business separate from the work we do for others.",
    cols: [
      { id: "ours", label: "Our business", hint: "MyJarvis itself", tone: T.green },
      { id: "clients", label: "Client work", hint: "paid engagements", tone: T.amber },
      { id: "ventures", label: "Ventures", hint: "our own bets", tone: T.blue },
      { id: "system", label: "The system", hint: "the OS we run on", tone: T.plum },
    ],
  },
};

const OURS = new Set(["jarvis", "ben", "leo"]);
const VENTURES = new Set(["sol", "remy"]);
const SYSTEM = new Set(["atlas", "dave"]);

function columnFor(view: View, p: KProject, hasDecision: boolean): string {
  const owner = (p.owner_agent || "").toLowerCase();
  const st = (p.status || "").toLowerCase();
  if (view === "attention") {
    if (hasDecision) return "needs";
    if (st === "blocked" || st === "waiting" || st === "paused") return "parked";
    return "moving";
  }
  if (view === "status") {
    if (st === "active" || st === "running") return "active";
    if (st === "waiting") return "waiting";
    if (st === "blocked") return "blocked";
    return "paused";
  }
  if (OURS.has(owner)) return "ours";
  if (VENTURES.has(owner)) return "ventures";
  if (SYSTEM.has(owner)) return "system";
  return "clients";
}

// ── card ─────────────────────────────────────────────────────────────────────
function Card({ p, plan, decision, size, tone }: { p: KProject; plan?: KPlan; decision?: KDecision; size: "lg" | "md" | "sm"; tone: string }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.slug });
  const route = BOARD_ROUTE[(p.owner_agent || "").toLowerCase()];
  const dot = STATUS_DOT[(p.status || "").toLowerCase()] ?? T.faint;
  const width = size === "lg" ? 380 : size === "md" ? 300 : 240;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        width, boxSizing: "border-box", background: T.card,
        border: `1px solid ${T.hair}`, borderLeft: `3px solid ${tone}`,
        borderRadius: 14, padding: size === "sm" ? 14 : 18,
        boxShadow: isDragging ? "0 18px 40px rgba(27,33,30,0.18)" : "0 4px 14px rgba(27,33,30,0.05)",
        fontFamily: FONT, cursor: "grab", touchAction: "none",
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.9 : 1,
        zIndex: isDragging ? 50 : undefined, position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: dot, flexShrink: 0 }} />
        <span style={{ fontSize: size === "sm" ? 15 : 17.5, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em", flex: 1, lineHeight: 1.2 }}>{p.name}</span>
        {route && (
          <a href={route} onPointerDown={(e) => e.stopPropagation()} style={{ display: "flex", color: T.faint }} title="Open its board">
            <ArrowUpRight style={{ width: 15, height: 15 }} />
          </a>
        )}
      </div>
      <div style={{ ...EYEBROW, marginTop: 6 }}>{plan?.role || cap(p.owner_agent || "") || "—"}</div>

      {size !== "sm" && (
        <div style={{ fontSize: 13.5, lineHeight: 1.45, color: T.ink2, marginTop: 10, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {p.current_focus || "No current focus set."}
        </div>
      )}

      {size === "lg" && plan?.today && (
        <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
          <span style={{ ...EYEBROW, color: T.green, width: 34, flexShrink: 0, paddingTop: 2 }}>Now</span>
          <span style={{ fontSize: 13.5, lineHeight: 1.4, color: T.ink }}>{plan.today}</span>
        </div>
      )}

      {size === "lg" && decision && (
        <div style={{ marginTop: 12, background: T.redWash, border: `1px solid ${T.red}`, borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ ...EYEBROW, color: T.red }}>Needs your call</div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, marginTop: 3, lineHeight: 1.3 }}>{decision.title}</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.4, color: T.ink2, marginTop: 4 }}>
            <span style={{ fontWeight: 600, color: T.greenDeep }}>Rec: </span>{decision.recommendation}
          </div>
        </div>
      )}
      {size === "md" && decision && (
        <div style={{ ...EYEBROW, color: T.red, marginTop: 10 }}>● Needs your call</div>
      )}
    </div>
  );
}

// ── column ───────────────────────────────────────────────────────────────────
function Column({ col, children, count }: { col: Col; children: React.ReactNode; count: number }) {
  const { isOver, setNodeRef } = useDroppable({ id: col.id });
  return (
    <div
      ref={setNodeRef}
      style={{
        background: isOver ? T.greenWash : T.bone,
        border: `1px solid ${isOver ? T.green : "transparent"}`,
        borderRadius: 16, padding: 14, minWidth: 268, alignSelf: "flex-start",
        transition: "background 120ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "2px 4px 12px" }}>
        <span style={{ width: 9, height: 9, borderRadius: 999, background: col.tone }} />
        <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>{col.label}</span>
        <span style={{ ...EYEBROW, color: T.faint }}>{count}</span>
      </div>
      {col.hint && <div style={{ fontSize: 11.5, color: T.faint, padding: "0 4px 12px", lineHeight: 1.35, maxWidth: 240 }}>{col.hint}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </div>
  );
}

// ── the switcher + board ─────────────────────────────────────────────────────
export function ProjectsKanban({ projects, plans, decisions }: { projects: KProject[]; plans: KPlan[]; decisions: KDecision[] }) {
  const [view, setView] = useState<View>("attention");
  // slug -> column, set by dragging. Per-view so each lens keeps its own moves.
  const [moved, setMoved] = useState<Record<View, Record<string, string>>>({ attention: {}, status: {}, portfolio: {} });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const planBy = useMemo(() => Object.fromEntries(plans.map((p) => [p.agent, p])), [plans]);
  const decByOwner = useMemo(() => {
    const m: Record<string, KDecision> = {};
    for (const d of decisions) {
      const k = (d.owner || "").toLowerCase();
      if (k && !m[k]) m[k] = d;
    }
    return m;
  }, [decisions]);

  const spec = VIEWS[view];
  const size: "lg" | "md" | "sm" = view === "attention" ? "lg" : view === "portfolio" ? "md" : "sm";

  const byCol = useMemo(() => {
    const m: Record<string, KProject[]> = {};
    for (const c of spec.cols) m[c.id] = [];
    for (const p of projects) {
      const owner = (p.owner_agent || "").toLowerCase();
      const natural = columnFor(view, p, !!decByOwner[owner]);
      const col = moved[view][p.slug] ?? natural;
      (m[col] ?? m[spec.cols[0].id]).push(p);
    }
    return m;
  }, [projects, view, moved, decByOwner, spec]);

  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over?.id;
    if (!over) return;
    setMoved((prev) => ({ ...prev, [view]: { ...prev[view], [String(e.active.id)]: String(over) } }));
  };

  return (
    // nowheel: scroll the columns, don't zoom the canvas. nodrag/nopan: a card
    // drag must not pan the board. pointerEvents: RF disables them otherwise.
    <div className="nowheel nodrag nopan" style={{ pointerEvents: "auto", fontFamily: FONT }}>
      {/* view switcher */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ display: "inline-flex", background: T.bone, borderRadius: 999, padding: 4, gap: 2 }}>
          {(Object.keys(VIEWS) as View[]).map((v) => (
            <button
              key={v}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setView(v)}
              style={{
                border: "none", cursor: "pointer", borderRadius: 999, padding: "7px 16px",
                fontFamily: FONT, fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.01em",
                background: view === v ? T.card : "transparent",
                color: view === v ? T.ink : T.faint,
                boxShadow: view === v ? "0 2px 8px rgba(27,33,30,0.08)" : "none",
              }}
            >
              {VIEWS[v].label}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.35 }}>{spec.blurb}</span>
      </div>
      <div style={{ ...EYEBROW, color: T.faint, marginBottom: 16 }}>
        Drag to re-bucket · in-session only, nothing is saved yet
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {spec.cols.map((c) => (
            <Column key={c.id} col={c} count={(byCol[c.id] ?? []).length}>
              {(byCol[c.id] ?? []).map((p) => (
                <Card
                  key={p.slug}
                  p={p}
                  plan={planBy[(p.owner_agent || "").toLowerCase()]}
                  decision={decByOwner[(p.owner_agent || "").toLowerCase()]}
                  size={size}
                  tone={c.tone}
                />
              ))}
            </Column>
          ))}
        </div>
      </DndContext>
    </div>
  );
}
